// src/components/admin/SchoolPlanEditor.js
//
// Admin ▸ Users ▸ 🏫 School / District plan.
//
// For schools and districts that pay by invoice / purchase order (never
// through Stripe). Sets users/{uid}.classPlan on one teacher or many at once
// via the setSchoolPlan Cloud Function (functions/schoolPlans.js):
//   type        School or District
//   studentCap  how many students the teacher's class can hold
//   name, poNumber   for your records
//   endDate     the plan stops on its own after this day (empty = no end)
// While it's on, the teacher and every student on their class list get full
// access and coins. Removing it (or the end date passing) drops them back to
// the free level — nothing is deleted.

import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/firebaseConfig";

const toMs = (v) => (v?.toMillis ? v.toMillis() : v?.toDate ? v.toDate().getTime() : v?._seconds ? v._seconds * 1000 : v ? new Date(v).getTime() : 0);
const ymd = (ms) => (ms ? new Date(ms).toISOString().slice(0, 10) : "");
// end of the coming school year: June 30
const defaultEnd = () => {
  const now = new Date();
  const y = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
  return `${y}-06-30`;
};

/** a one-line summary for tables and the details pop-up */
export function schoolPlanSummary(cp) {
  if (!cp || !Number(cp.studentCap)) return "";
  const end = toMs(cp.endDate);
  const over = end && end <= Date.now();
  return `${cp.type === "district" ? "District" : "School"} · ${cp.studentCap} students${cp.name ? ` · ${cp.name}` : ""}${
    end ? ` · ${over ? "ended" : "until"} ${new Date(end).toLocaleDateString()}` : ""
  }`;
}

export default function SchoolPlanEditor({ uids = [], current = null, label = "", onClose, onSaved }) {
  const [type, setType] = useState(current?.type || "school");
  const [cap, setCap] = useState(String(current?.studentCap || 25));
  const [name, setName] = useState(current?.name || "");
  const [po, setPo] = useState(current?.poNumber || "");
  const [end, setEnd] = useState(current ? ymd(toMs(current.endDate)) : defaultEnd());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const save = async (remove = false) => {
    if (!uids.length) return;
    if (!remove && !(Number(cap) >= 1)) {
      setError("Class size must be at least 1.");
      return;
    }
    if (remove && !window.confirm(`Remove the school plan from ${uids.length === 1 ? "this teacher" : `${uids.length} teachers`}? They and their students go back to the free level (nothing is deleted).`)) return;
    setBusy(true);
    setError("");
    try {
      const plan = remove
        ? null
        : { type, studentCap: Number(cap), name: name.trim(), poNumber: po.trim(), endDate: end ? `${end}T23:59:59` : null };
      const { data } = await httpsCallable(functions, "setSchoolPlan")({ uids, plan });
      setDone(remove ? `Plan removed from ${data.updated} account${data.updated === 1 ? "" : "s"}.` : `Plan set on ${data.updated} account${data.updated === 1 ? "" : "s"}.`);
      onSaved?.(uids, plan);
    } catch (e) {
      setError(String(e?.message || e).replace(/^.*?:\s*/, ""));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={st.backdrop} role="dialog" aria-modal="true" aria-label="School or district plan">
      <div style={st.box}>
        <h2 style={st.h2}>🏫 School / District plan</h2>
        <p style={st.muted}>
          {label || (uids.length === 1 ? "1 teacher" : `${uids.length} teachers`)} · for schools paying by invoice or purchase order. While it's on, the
          teacher and every student in their class get full access and earn coins.
        </p>

        {done ? (
          <p style={st.ok}>✓ {done}</p>
        ) : (
          <>
            <div style={st.grid}>
              <label style={st.field}>
                Plan
                <select value={type} onChange={(e) => setType(e.target.value)} style={st.input}>
                  <option value="school">School</option>
                  <option value="district">District</option>
                </select>
              </label>
              <label style={st.field}>
                Class size (students per teacher)
                <input type="number" min={1} max={500} value={cap} onChange={(e) => setCap(e.target.value)} style={st.input} />
              </label>
              <label style={st.field}>
                {type === "district" ? "District name" : "School name"}
                <input value={name} onChange={(e) => setName(e.target.value)} style={st.input} placeholder="e.g. Oak Hill Elementary" />
              </label>
              <label style={st.field}>
                PO / invoice number
                <input value={po} onChange={(e) => setPo(e.target.value)} style={st.input} placeholder="optional" />
              </label>
              <label style={{ ...st.field, gridColumn: "1 / -1" }}>
                Ends on <small style={st.small}>the plan switches off by itself after this day — leave empty for no end</small>
                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={st.input} />
              </label>
            </div>
            {current && <p style={st.muted}>Now: {schoolPlanSummary(current) || "none"}</p>}
            {error && <p style={st.err}>{error}</p>}
          </>
        )}

        <div style={st.actions}>
          {!done && current && (
            <button type="button" onClick={() => save(true)} disabled={busy} style={{ ...st.btn, ...st.danger }}>
              Remove plan
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose} disabled={busy} style={{ ...st.btn, ...st.ghost }}>
            {done ? "Close" : "Cancel"}
          </button>
          {!done && (
            <button type="button" onClick={() => save(false)} disabled={busy || !uids.length} style={st.btn}>
              {busy ? "Saving…" : current ? "Update plan" : "Set plan"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const st = {
  backdrop: { position: "fixed", inset: 0, zIndex: 10050, background: "rgba(15,23,42,0.5)", display: "grid", placeItems: "center", padding: 16 },
  box: { width: "min(560px, 100%)", background: "#fff", borderRadius: 16, padding: 20, boxShadow: "0 30px 70px rgba(15,23,42,0.3)", color: "#0f172a" },
  h2: { margin: "0 0 6px", fontSize: 22 },
  muted: { color: "#64748b", fontSize: 14, margin: "0 0 12px" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  field: { display: "flex", flexDirection: "column", gap: 4, fontWeight: 700, fontSize: 13.5 },
  small: { fontWeight: 400, color: "#64748b" },
  input: { font: "inherit", fontSize: 14, padding: "8px 10px", border: "1.5px solid #cbd5e1", borderRadius: 10 },
  ok: { background: "#dcfce7", color: "#166534", padding: "10px 12px", borderRadius: 10, fontWeight: 700 },
  err: { color: "#b91c1c", fontWeight: 700 },
  actions: { display: "flex", gap: 8, marginTop: 16, alignItems: "center" },
  btn: { border: 0, borderRadius: 10, padding: "9px 16px", fontWeight: 800, cursor: "pointer", background: "#2563eb", color: "#fff", font: "inherit" },
  ghost: { background: "#e2e8f0", color: "#0f172a" },
  danger: { background: "#fee2e2", color: "#b91c1c" },
};
